/**
 * Invisible occluder faces that stop elevation auto-annotations from "seeing through"
 * aligned openings and tagging far-side entities (e.g. a rear door's View Note) onto the
 * wrong elevation. Each exterior opening is sealed just behind its door; the occluders
 * carry no `entityId`, so they block the view but are never tagged.
 * @module
 */
import type { Building } from "building-types/building/buildingTypes";
import type { OpeningSide } from "building-types/openings/openingTypes";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import { ONE_INCH } from "common/constants/scaleFactors";
import { faceNew, type Face } from "geom/brep";
import { planeCopyMoveAlongNormal, planeSignedDistanceTo, polyLine2OnPlane } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polyLine2EdgeCount } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { UVGeneration } from "geom/uv";
import { faceGroupBSPNew, type FaceGroupBSP } from "lib/house/faceGroupBSP";

const MIN_LOOP_EDGES = 3;
// Clearance past the interior face so the occluder sits behind the door's own faces.
const INTERIOR_OFFSET = ONE_INCH;

interface OpeningSides {
  readonly exteriorPlane: Plane;
  readonly exteriorLoop: PolyLine2;
  readonly interiorPlane: Plane;
  readonly interiorLoop: PolyLine2;
}

function isExteriorSide(side: OpeningSide, hasRoom: boolean): boolean {
  // `condition` is authoritative; fall back to "no room on this side" when unresolved.
  return side.context.condition === "EXTERIOR" || (side.context.condition === undefined && !hasRoom);
}

// Returns undefined for purely interior openings (rooms both sides) — they need no occluder.
function classifyOpeningSides(opening: WallOpening): OpeningSides | undefined {
  const frontExterior = isExteriorSide(opening.front, !!opening.frontRoom);
  const backExterior = isExteriorSide(opening.back, !!opening.backRoom);

  if (frontExterior && !backExterior) {
    return {
      exteriorPlane: opening.frontPlane,
      exteriorLoop: opening.front.opening2,
      interiorPlane: opening.backPlane,
      interiorLoop: opening.back.opening2,
    };
  }
  if (backExterior && !frontExterior) {
    return {
      exteriorPlane: opening.backPlane,
      exteriorLoop: opening.back.opening2,
      interiorPlane: opening.frontPlane,
      interiorLoop: opening.front.opening2,
    };
  }
  // Both sides exterior (e.g. dormer/gable openings, or an explicit through-wall punch-through):
  // there's no interior side to seal toward, so we intentionally skip — no occluder, no warning.
  return undefined;
}

// `planeSwapUV` and `swapLoopXY` are a matched pair and must be used together: swapping the U/V
// basis reverses the plane normal, and swapping the loop's XY compensates so the identical 2D
// shape maps to the same 3D points in the flipped coordinate system. Using one without the other
// would mirror the occluder.
//
// Same plane with the U/V basis swapped, reversing the normal while keeping the origin.
function planeSwapUV(plane: Plane): Plane {
  return {
    originX: plane.originX,
    originY: plane.originY,
    originZ: plane.originZ,
    uX: plane.vX,
    uY: plane.vY,
    uZ: plane.vZ,
    vX: plane.uX,
    vY: plane.uY,
    vZ: plane.uZ,
  };
}

function swapLoopXY(loop: PolyLine2): PolyLine2 {
  const swapped = new Float64Array(loop.length);
  for (let i = 0; i < loop.length; i += 2) {
    swapped[i] = loop[i + 1];
    swapped[i + 1] = loop[i];
  }
  return swapped;
}

function buildOccluderFaces(sides: OpeningSides): Face[] {
  const { exteriorPlane, exteriorLoop, interiorPlane, interiorLoop } = sides;
  if (polyLine2EdgeCount(exteriorLoop) < MIN_LOOP_EDGES) {
    return [];
  }

  // interiorLoop is not edge-count guarded like exteriorLoop: if it's degenerate, interiorPts has
  // < 3 points and we fall back to placing the occluder on the exterior plane itself. Acceptable
  // degraded behavior — it still occludes, just flush with the opening rather than just inside.
  const interiorPts = polyLine2OnPlane(interiorPlane, interiorLoop);
  let placementPlane = exteriorPlane;
  if (interiorPts.length >= 3) {
    // Signed (not absolute) distance so we move toward the interior, not always +normal.
    const toInterior = planeSignedDistanceTo(exteriorPlane, interiorPts[0], interiorPts[1], interiorPts[2]);
    // `|| 1` guards a zero-thickness wall (coincident planes, toInterior === 0): default the sign
    // to +1 so we still offset by an inch rather than collapsing to no offset.
    const offset = toInterior + Math.sign(toInterior || 1) * INTERIOR_OFFSET;
    placementPlane = planeCopyMoveAlongNormal(exteriorPlane, offset);
  }

  // Double-sided so the occluder works for whichever elevation looks at the opening.
  return [faceNew([exteriorLoop], placementPlane), faceNew([swapLoopXY(exteriorLoop)], planeSwapUV(placementPlane))];
}

export function buildOpeningOccluderFaceGroups(building: Building): FaceGroupBSP[] {
  const openings: WallOpening[] = [];
  for (const placement of Object.values(building.doorPlacements)) {
    if (placement.opening) {
      openings.push(placement.opening);
    }
  }
  for (const placement of Object.values(building.windowPlacements)) {
    if (placement.opening) {
      openings.push(placement.opening);
    }
  }

  // Occluder face-group properties: no material, and no entityId so they occlude but are never tagged.
  const designMaterialId = null;
  const uvGen = UVGeneration.NONE;
  const materialTransformation = undefined;
  const entityId = undefined;
  const generateNeighbors = false;

  const groups: FaceGroupBSP[] = [];
  for (const opening of openings) {
    const sides = classifyOpeningSides(opening);
    if (!sides) {
      continue;
    }
    const faces = buildOccluderFaces(sides);
    if (faces.length > 0) {
      groups.push(faceGroupBSPNew(faces, designMaterialId, uvGen, materialTransformation, entityId, generateNeighbors));
    }
  }

  return groups;
}
